import React from "react";
import { toast } from "sonner";
import { X, Rocket, ArrowSquareOut, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-75.10 — Pitch-to-Live modal.
 *
 * Turns any sample landing page into a real branded custom page. Backend
 * endpoint spins up a client, an email destination, and a published
 * cloned page — this modal collects the operator's inputs and surfaces
 * the resulting URL for one-click share.
 *
 * Props:
 *   samplePage:  the row the operator clicked "Pitch → Live" on
 *   onClose:     () => void
 *   onCreated:   ({page_id, slug, url}) => void — parent refreshes the
 *                Custom-buyer-pages table so the new row appears.
 */
export default function PitchToLiveModal({ samplePage, onClose, onCreated }) {
  const [firmName, setFirmName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [destinationEmail, setDestinationEmail] = React.useState("");
  const [brandColor, setBrandColor] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const submit = async () => {
    const firm = firmName.trim();
    const ph = phone.trim();
    const em = destinationEmail.trim();
    if (!firm) return toast.error("Firm name is required");
    if (!ph) return toast.error("Phone is required");
    if (!em || !em.includes("@")) return toast.error("Valid email required");
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/custom-landing-pages/${samplePage.id}/pitch-to-live`,
        { firm_name: firm, phone: ph, destination_email: em,
          brand_color: brandColor.trim() || undefined },
      );
      setResult(data);
      toast.success(`Live page created — /lp/${data.slug}`);
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pitch-to-live failed");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(result?.url || "");
    toast.success("URL copied");
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center overflow-y-auto p-4"
      data-testid="pitch-to-live-modal"
    >
      <div className="bg-white border-2 border-black shadow-[8px_8px_0_#000] w-full max-w-2xl my-8">
        <div className="flex items-center justify-between border-b-2 border-black p-4">
          <div>
            <div className="font-chivo font-black uppercase tracking-tight text-lg inline-flex items-center gap-2">
              <Rocket size={16} weight="fill" className="text-emerald-700" />
              Pitch → Live
            </div>
            <div className="label-mono">
              turn <span className="font-mono">/lp/{samplePage.slug}</span> into a real branded page
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pitch-to-live-close"
            className="border border-black p-2 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!result && (
            <>
              <div className="border-l-2 border-emerald-700 bg-emerald-50/60 px-3 py-2 text-[11px] leading-relaxed text-zinc-800">
                <b>What happens:</b> LeadFlow spins up a real client org
                named <b>{firmName || "your firm"}</b>, an email delivery
                destination pointed at your inbox, and a published clone
                of this sample — ready to share as an ad landing page.
                Nothing about the source sample changes.
              </div>

              <Field label="Firm name *" testId="pitch-firm-input">
                <input
                  type="text"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="Smith Injury Law"
                  className="w-full border border-black px-3 py-2 font-mono text-[12px] bg-white"
                  data-testid="pitch-firm-input-field"
                />
              </Field>

              <Field label="Firm phone (shown as header CTA) *" testId="pitch-phone-input">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="1-800-555-0110"
                  className="w-full border border-black px-3 py-2 font-mono text-[12px] bg-white"
                  data-testid="pitch-phone-input-field"
                />
              </Field>

              <Field
                label="Where should leads be delivered? *"
                testId="pitch-email-input"
                hint="Every lead this page captures will be emailed here."
              >
                <input
                  type="email"
                  value={destinationEmail}
                  onChange={(e) => setDestinationEmail(e.target.value)}
                  placeholder="intake@smithinjurylaw.com"
                  className="w-full border border-black px-3 py-2 font-mono text-[12px] bg-white"
                  data-testid="pitch-email-input-field"
                />
              </Field>

              <Field
                label="Brand color (optional)"
                testId="pitch-brand-color-input"
                hint="Hex like #0f766e — leave blank to keep the sample's color."
              >
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  placeholder="#111111"
                  className="w-full border border-black px-3 py-2 font-mono text-[12px] bg-white"
                  data-testid="pitch-brand-color-input-field"
                />
              </Field>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="border border-black px-3 py-2 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy}
                  data-testid="pitch-to-live-submit"
                  className="bg-emerald-700 text-white border-2 border-black px-4 py-2 uppercase text-[10px] tracking-widest font-bold inline-flex items-center gap-2 disabled:opacity-40 hover:bg-emerald-800"
                >
                  <Rocket size={12} weight="fill" />
                  {busy ? "Publishing…" : "Publish live page"}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="pitch-to-live-result">
              <div className="border-2 border-emerald-700 bg-emerald-50 p-4">
                <div className="font-chivo font-black uppercase tracking-tight text-sm inline-flex items-center gap-2 mb-2">
                  <Rocket size={14} weight="fill" className="text-emerald-700" />
                  Page live — share the URL below
                </div>
                <div className="text-[11px] text-zinc-700 leading-relaxed">
                  A new client (<b>{result.client_name}</b>) and delivery
                  destination were provisioned. This page will NOT show
                  the "Demo" chip on the admin table — it's a real
                  landing page now.
                </div>
              </div>

              <ResultRow label="Live URL" value={result.url} testId="pitch-result-url" primary />
              <ResultRow label="Slug"      value={result.slug} testId="pitch-result-slug" />
              <ResultRow label="Client"    value={result.client_name} testId="pitch-result-client" />
              <ResultRow label="Destination" value={result.destination_name} testId="pitch-result-destination" />

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={copyUrl}
                  data-testid="pitch-result-copy-url"
                  className="border-2 border-black bg-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold inline-flex items-center gap-2 hover:bg-black hover:text-white"
                >
                  <Copy size={12} weight="bold" /> Copy URL
                </button>
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pitch-result-open"
                  className="border-2 border-black bg-emerald-700 text-white px-3 py-2 uppercase text-[10px] tracking-widest font-bold inline-flex items-center gap-2 hover:bg-emerald-800"
                >
                  <ArrowSquareOut size={12} weight="bold" /> Open live
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  className="border border-black px-3 py-2 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="label-mono mb-1">{label}</div>
      {children}
      {hint && (
        <div className="text-[10.5px] text-zinc-500 mt-1">{hint}</div>
      )}
    </div>
  );
}

function ResultRow({ label, value, testId, primary = false }) {
  const copy = () => {
    navigator.clipboard.writeText(value || "");
    toast.success(`${label} copied`);
  };
  return (
    <div className="border border-black p-3 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <div className={
          "text-[10px] uppercase tracking-widest px-2 py-0.5 font-bold "
          + (primary ? "bg-emerald-700 text-white" : "bg-zinc-100")
        }>
          {label}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value || ""}
          className={
            "flex-1 border border-black px-2 py-1 font-mono text-[11px] "
            + (primary ? "bg-emerald-50 font-bold" : "bg-zinc-50")
          }
          data-testid={`${testId}-value`}
        />
        <button
          type="button"
          onClick={copy}
          data-testid={`${testId}-copy`}
          className="border border-black p-2 hover:bg-black hover:text-white"
          title="Copy"
        >
          <Copy size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
